import React, { useState, useEffect, useCallback } from 'react';
import { Package, Search, Filter, Check, X, ShoppingBag, AlertTriangle, Archive } from 'lucide-react';
import { broadcastStockChange, subscribeToStockChanges } from '../../utils/stockSync';
import { getToken } from '../../utils/secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Separate component for stock input with local state management
function StockInput({ productId, initialValue, onUpdate }) {
  const [value, setValue] = useState(initialValue || 0);
  const [originalValue, setOriginalValue] = useState(initialValue || 0);
  
  // Update local state when product data changes from external source
  useEffect(() => {
    setValue(initialValue || 0);
    setOriginalValue(initialValue || 0);
  }, [initialValue]);
  
  const handleBlur = useCallback(() => {
    // Only trigger update if value actually changed
    if (value !== originalValue) {
      onUpdate(productId, value);
      setOriginalValue(value); // Update original to prevent duplicate calls
    }
  }, [productId, value, originalValue, onUpdate]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Trigger blur on Enter key
    }
  };
  
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => setValue(parseInt(e.target.value) || 0)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="w-20 px-2 py-1 text-center border border-gray-300 rounded text-sm"
      min="0"
      data-testid={`stock-input-${productId}`}
    />
  );
}

export default function InventoryTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_products: 0,
    in_stock: 0,
    low_stock: 0,
    out_of_stock: 0,
    by_order: 0
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 50
      });
      if (filter !== 'all') {
        params.append('stock_status', filter);
      }
      
      const response = await fetch(`${API_URL}/api/products/inventory/all?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
        setSummary(data.summary);
        setPagination(data.pagination);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [filter, pagination.page]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Subscribe to stock changes from other pages/tabs
  useEffect(() => {
    const unsubscribe = subscribeToStockChanges(() => {
      fetchInventory();
    });
    return () => unsubscribe();
  }, [fetchInventory]);

  const updateStock = async (productId, quantity) => {
    try {
      const token = getToken('token');
      const response = await fetch(`${API_URL}/api/products/${productId}/stock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stock_quantity: quantity })
      });
      
      if (response.ok) {
        // Broadcast the stock change to other tabs/pages
        broadcastStockChange(productId, { stock_quantity: quantity, action: 'stock-update' });
        fetchInventory();
      }
    } catch (error) {
    }
  };

  const toggleSoldOut = async (productId, isSoldOut) => {
    try {
      const token = getToken('token');
      await fetch(`${API_URL}/api/products/${productId}/sold-out`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_sold_out: isSoldOut })
      });
      fetchInventory();
    } catch (error) {
    }
  };

  // Handle Mark Available / Mark Sold action
  // - Mark Sold: Set stock to 0, is_sold_out to true
  // - Mark Available: Set stock to 1, is_sold_out to false
  const handleMarkAction = async (productId, isCurrentlyUnavailable) => {
    try {
      const token = getToken('token');
      
      if (isCurrentlyUnavailable) {
        // Mark Available: Set stock to 1, remove sold_out flag
        await fetch(`${API_URL}/api/products/${productId}/mark-available`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        // Broadcast the change to other tabs/pages
        broadcastStockChange(productId, { stock_quantity: 1, is_sold_out: false, action: 'mark-available' });
      } else {
        // Mark Sold: Set stock to 0, set sold_out flag
        await fetch(`${API_URL}/api/products/${productId}/mark-sold`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        // Broadcast the change to other tabs/pages
        broadcastStockChange(productId, { stock_quantity: 0, is_sold_out: true, action: 'mark-sold' });
      }
      fetchInventory();
    } catch (error) {
    }
  };

  const toggleByOrder = async (productId, isByOrder) => {
    try {
      const token = getToken('token');
      await fetch(`${API_URL}/api/products/${productId}/by-order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_by_order: isByOrder })
      });
      // Broadcast the change to other tabs/pages
      broadcastStockChange(productId, { is_by_order: isByOrder, action: 'toggle-by-order' });
      fetchInventory();
    } catch (error) {
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedProducts.length === 0) return;
    
    try {
      const token = getToken('token');
      await fetch(`${API_URL}/api/products/inventory/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_ids: selectedProducts,
          action: bulkAction
        })
      });
      // Broadcast bulk change to other tabs/pages
      selectedProducts.forEach(productId => {
        broadcastStockChange(productId, { action: `bulk-${bulkAction}` });
      });
      setSelectedProducts([]);
      setBulkAction('');
      fetchInventory();
    } catch (error) {
    }
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const toggleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const getStockStatus = (product) => {
    if (product.is_sold_out) return { label: 'Sold Out', color: 'red' };
    if (product.is_by_order) return { label: 'By Order', color: 'blue' };
    // Treat undefined, null, or 0 as out of stock
    const stock = product.stock_quantity ?? 0;
    if (stock <= 0) return { label: 'Out of Stock', color: 'red' };
    if (stock <= 5) return { label: 'Low Stock', color: 'yellow' };
    return { label: 'In Stock', color: 'green' };
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="inventory-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6 flex items-center">
        <Package className="h-7 w-7 mr-3" />
        Inventory Management
      </h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
          <p className="text-2xl font-bold text-[#1a3a5c]">{summary.total_products}</p>
          <p className="text-sm text-gray-500">Total Products</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
          <p className="text-2xl font-bold text-green-600">{summary.in_stock}</p>
          <p className="text-sm text-green-700">In Stock</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
          <p className="text-2xl font-bold text-yellow-600">{summary.low_stock}</p>
          <p className="text-sm text-yellow-700">Low Stock</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
          <p className="text-2xl font-bold text-red-600">{summary.out_of_stock}</p>
          <p className="text-sm text-red-700">Out of Stock</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
          <p className="text-2xl font-bold text-blue-600">{summary.by_order}</p>
          <p className="text-sm text-blue-700">By Order</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#1a3a5c]"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPagination({...pagination, page: 1}); }}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#1a3a5c]"
            >
              <option value="all">All Products</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock (≤5)</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="by_order">By Order</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedProducts.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm text-gray-600">{selectedProducts.length} selected</span>
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Select Action</option>
              <option value="mark_sold">Mark as Sold Out</option>
              <option value="mark_available">Mark as Available</option>
              <option value="mark_by_order">Mark as By Order</option>
              <option value="remove_by_order">Remove By Order</option>
            </select>
            <button
              onClick={handleBulkAction}
              disabled={!bulkAction}
              className="px-4 py-1 bg-[#1a3a5c] text-white rounded-md text-sm hover:bg-[#1f4268] disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === products.length && products.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Product</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Stock</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">By Order</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No products found</td>
                </tr>
              ) : (
                filteredProducts.map(product => {
                  const status = getStockStatus(product);
                  return (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => toggleSelectProduct(product.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.images?.[0] && (
                            <img 
                              src={product.images[0]} 
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.metal_type} {product.metal_carat}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{product.category}</td>
                      <td className="px-4 py-3 text-center">
                        <StockInput
                          productId={product.id}
                          initialValue={product.stock_quantity}
                          onUpdate={updateStock}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status.color === 'green' ? 'bg-green-100 text-green-800' :
                          status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          status.color === 'red' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={product.is_by_order || false}
                            onChange={(e) => toggleByOrder(product.id, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          // Determine if product is unavailable (out of stock OR sold out)
                          const stock = product.stock_quantity ?? 0;
                          const isOutOfStock = stock <= 0 && !product.is_by_order;
                          const isUnavailable = product.is_sold_out || isOutOfStock;
                          
                          return (
                            <button
                              onClick={() => handleMarkAction(product.id, isUnavailable)}
                              className={`px-3 py-1 rounded text-xs font-medium ${
                                isUnavailable 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              {isUnavailable ? 'Mark Available' : 'Mark Sold'}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.pages} ({pagination.total} products)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
